// crewbit-admin/src/pages/Reports/MileageReportPage.tsx
//
// Schedule Module Phase C7.5 — T2125-shaped mileage report for admin.
// Reads entry_mileage_segments; groups by operator + month.
// Spec: docs/sessions/SESSION_SCHEDULE_PHASE_C7_PROMPT.md §4.2.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { palette, admin } from '../../styles/tokens';

interface MileageRow {
  id: string;
  user_id: string;
  distance_m: number;
  actual_drive_seconds: number | null;
  started_at: string;
  ended_at: string;
  from_entry_id: string | null;
  to_entry_id: string | null;
}

interface OperatorInfo {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

interface MonthBucket {
  month: string; // YYYY-MM
  totalKm: number;
  totalDriveMinutes: number;
  segments: number;
}

interface OperatorSummary {
  operator: OperatorInfo;
  months: MonthBucket[];
  totalKm: number;
  totalSegments: number;
}

type RangeKey = 'month' | 'quarter' | 'year';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'year', label: 'This Year' },
];

function rangeStart(key: RangeKey): string {
  const now = new Date();
  if (key === 'month') return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  if (key === 'quarter') {
    const q = Math.floor(now.getMonth() / 3) * 3;
    return `${now.getFullYear()}-${String(q + 1).padStart(2, '0')}-01`;
  }
  return `${now.getFullYear()}-01-01`;
}

export function MileageReportPage() {
  const businessId = useAuthStore((s) => s.business?.id);
  const [rows, setRows] = useState<MileageRow[]>([]);
  const [operators, setOperators] = useState<OperatorInfo[]>([]);
  const [range, setRange] = useState<RangeKey>('month');
  const [loading, setLoading] = useState(true);
  const [mileageRate, setMileageRate] = useState<number>(0.70);

  const fetchData = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);

    const start = rangeStart(range);

    const [segRes, opRes, rateRes] = await Promise.all([
      supabase
        .from('entry_mileage_segments')
        .select('id, user_id, distance_m, actual_drive_seconds, started_at, ended_at, from_entry_id, to_entry_id')
        .eq('business_id', businessId)
        .gte('started_at', start)
        .order('started_at', { ascending: true }),
      supabase
        .from('users')
        .select('id, first_name, last_name')
        .eq('business_id', businessId),
      supabase
        .from('business_financial_settings')
        .select('mileage_rate_1')
        .eq('business_id', businessId)
        .maybeSingle(),
    ]);

    setRows((segRes.data as MileageRow[]) ?? []);
    setOperators((opRes.data as OperatorInfo[]) ?? []);
    if (rateRes.data?.mileage_rate_1 != null) {
      setMileageRate(Number(rateRes.data.mileage_rate_1));
    }
    setLoading(false);
  }, [businessId, range]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- initial async fetch on mount
  useEffect(() => { document.title = 'CrewBit · Mileage Report'; void fetchData(); }, [fetchData]);

  const summaries = useMemo((): OperatorSummary[] => {
    const byOp = new Map<string, MileageRow[]>();
    for (const row of rows) {
      const arr = byOp.get(row.user_id) ?? [];
      arr.push(row);
      byOp.set(row.user_id, arr);
    }

    return Array.from(byOp.entries()).map(([userId, opRows]) => {
      const op = operators.find((o) => o.id === userId) ?? { id: userId, first_name: null, last_name: null };
      const monthMap = new Map<string, MonthBucket>();

      for (const r of opRows) {
        const m = r.started_at.slice(0, 7);
        const bucket = monthMap.get(m) ?? { month: m, totalKm: 0, totalDriveMinutes: 0, segments: 0 };
        bucket.totalKm += r.distance_m / 1000;
        bucket.totalDriveMinutes += (r.actual_drive_seconds ?? 0) / 60;
        bucket.segments += 1;
        monthMap.set(m, bucket);
      }

      const months = Array.from(monthMap.values()).sort((a, b) => a.month.localeCompare(b.month));
      const totalKm = months.reduce((s, m) => s + m.totalKm, 0);
      const totalSegments = months.reduce((s, m) => s + m.segments, 0);

      return { operator: op, months, totalKm, totalSegments };
    }).sort((a, b) => b.totalKm - a.totalKm);
  }, [rows, operators]);

  const grandTotalKm = summaries.reduce((s, o) => s + o.totalKm, 0);
  const estimatedDeduction = grandTotalKm * mileageRate;

  const exportCSV = useCallback(() => {
    const header = 'Operator,Month,Segments,Distance (km),Drive Time (min),Est. Deduction';
    const lines = summaries.flatMap((s) =>
      s.months.map((m) => {
        const name = [s.operator.first_name, s.operator.last_name].filter(Boolean).join(' ') || 'Unknown';
        return `"${name}",${m.month},${m.segments},${m.totalKm.toFixed(1)},${Math.round(m.totalDriveMinutes)},$${(m.totalKm * mileageRate).toFixed(2)}`;
      }),
    );
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mileage-report-${rangeStart(range)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [summaries, mileageRate, range]);

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: admin.header, margin: 0 }}>
          Mileage Report
        </h1>
        <button
          onClick={exportCSV}
          disabled={loading || summaries.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: `1px solid ${admin.border}`,
            background: admin.surface, cursor: 'pointer', fontSize: 13, fontWeight: 500,
            color: admin.body, opacity: loading || summaries.length === 0 ? 0.5 : 1,
          }}
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      <p style={{ fontSize: 14, color: admin.caption, marginBottom: 20 }}>
        T2125 line 9281 — motor vehicle expenses. Per-operator mileage from GPS tracking.
      </p>

      {/* Range tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: range === r.key ? palette.accent : palette.g100,
              color: range === r.key ? palette.white : admin.body,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        <SummaryCard label="Total distance" value={`${grandTotalKm.toFixed(1)} km`} />
        <SummaryCard label="Operators tracked" value={String(summaries.length)} />
        <SummaryCard label="Est. deduction (line 9281)" value={`$${estimatedDeduction.toFixed(2)}`} accent />
      </div>

      {loading ? (
        <p style={{ color: admin.caption, fontSize: 14 }}>Loading...</p>
      ) : summaries.length === 0 ? (
        <p style={{ color: admin.caption, fontSize: 14 }}>
          No mileage data for this period. Mileage is captured when operators use Auto Features during route execution.
        </p>
      ) : (
        <div style={{
          backgroundColor: admin.surface, border: `1px solid ${admin.border}`,
          borderRadius: 12, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Operator</th>
                <th style={TH}>Month</th>
                <th style={{ ...TH, textAlign: 'right' }}>Segments</th>
                <th style={{ ...TH, textAlign: 'right' }}>Distance</th>
                <th style={{ ...TH, textAlign: 'right' }}>Drive time</th>
                <th style={{ ...TH, textAlign: 'right' }}>Est. deduction</th>
              </tr>
            </thead>
            <tbody>
              {summaries.map((s) =>
                s.months.map((m, i) => (
                  <tr key={`${s.operator.id}-${m.month}`}>
                    {i === 0 ? (
                      <td style={{ ...TD, fontWeight: 600 }} rowSpan={s.months.length}>
                        {[s.operator.first_name, s.operator.last_name].filter(Boolean).join(' ') || 'Unknown'}
                      </td>
                    ) : null}
                    <td style={TD}>{m.month}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{m.segments}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{m.totalKm.toFixed(1)} km</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{Math.round(m.totalDriveMinutes)} min</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 500 }}>
                      ${(m.totalKm * mileageRate).toFixed(2)}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: 12, color: admin.caption, marginTop: 16 }}>
        Rate: ${mileageRate.toFixed(2)}/km (from Tax & compliance settings). GPS-segment method — actual distance driven, not straight-line.
      </p>
    </div>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{
      padding: 16, borderRadius: 12,
      backgroundColor: accent ? palette.accentWash : admin.surface,
      border: `1px solid ${accent ? palette.accentSoft : admin.border}`,
    }}>
      <div style={{ fontSize: 12, color: admin.caption, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent ? palette.accent : admin.header }}>{value}</div>
    </div>
  );
}

const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: admin.caption, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left',
  borderBottom: `1px solid ${admin.border}`, whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 14px',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};

export default MileageReportPage;
